import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "@/App.css";
import "./lib/i18n";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";

import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { UserAuthProvider, useUserAuth } from "./context/UserAuthContext";
import { userApi } from "./lib/api";

import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Categories } from "./components/Categories";
import { Bestsellers } from "./components/Bestsellers";
import { JustDropped } from "./components/JustDropped";
import ShopPage from "./components/ShopPage";
import ManifestoPage from "./components/pages/ManifestoPage";
import DropClubPage from "./components/pages/DropClubPage";
import LookbookPage from "./components/pages/LookbookPage";
import ContattiPage from "./components/pages/ContattiPage";
import SpedizioniPage from "./components/pages/SpedizioniPage";
import ResiPage from "./components/pages/ResiPage";
import AutenticitaPage from "./components/pages/AutenticitaPage";
import FaqPage from "./components/pages/FaqPage";
import { About } from "./components/About";
import { Store } from "./components/Store";
import { Newsletter } from "./components/Newsletter";
import { Footer } from "./components/Footer";
import { CartDrawer } from "./components/CartDrawer";
import { CardGateMount } from "./components/CardGateMount";

import UserLoginPage from "./components/UserLoginPage";
import AccountPage from "./components/AccountPage";
import ProductDetailPage from "./components/ProductDetailPage";
import CheckoutSuccessPage from "./components/CheckoutSuccessPage";
import CheckoutCancelPage from "./components/CheckoutCancelPage";

import AdminLogin from "./components/admin/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import Dashboard from "./components/admin/Dashboard";
import ProductsPage from "./components/admin/ProductsPage";
import ImportProductsPage from "./components/admin/ImportProductsPage";
import GoogleSheetSyncPage from "./components/admin/GoogleSheetSyncPage";
import OrdersPage from "./components/admin/OrdersPage";
import AdminsPage from "./components/admin/AdminsPage";
import NewsletterPage from "./components/admin/NewsletterPage";
import CardRequestsPage from "./components/admin/CardRequestsPage";
import { PipelinePage, ContactsPage } from "./components/admin/CrmPages";
import IntegrationsPage from "./components/admin/IntegrationsPage";
import { ChatWidget } from "./components/ChatWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PublicSite() {
  const { user } = useUserAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlistIds, setWishlistIds] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`)
      .then((r) => setProducts(r.data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const loadWishlist = useCallback(async () => {
    if (!user) { setWishlistIds([]); return; }
    try {
      const { data } = await userApi.get("/users/me/wishlist");
      setWishlistIds(data.map((p) => p.id));
    } catch {}
  }, [user]);

  useEffect(() => { loadWishlist(); }, [loadWishlist]);

  return (
    <CartProvider>
      <div className="App bg-zinc-950 min-h-screen text-white">
        <Navbar />
        <main>
          <Hero />
          <Categories />
          {loading ? (
            <div className="text-center py-32 font-heading text-3xl text-zinc-500 uppercase">...</div>
          ) : (
            <>
              <JustDropped products={products} wishlistIds={wishlistIds} onWishlistChange={loadWishlist} />
              <Bestsellers products={products} wishlistIds={wishlistIds} onWishlistChange={loadWishlist} />
            </>
          )}
          <About />
          <Store />
          <Newsletter />
        </main>
        <Footer />
        <CartDrawer />
        <CardGateMount />
        <ChatWidget />
      </div>
    </CartProvider>
  );
}

function ProductDetailWrapper() {
  return (
    <CartProvider>
      <ProductDetailPage />
      <CartDrawer />
      <CardGateMount />
      <ChatWidget />
    </CartProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="bottom-right" theme="dark"
        toastOptions={{
          style: { background: "#09090b", border: "2px solid #facc15", borderRadius: 0, color: "#fafafa", fontFamily: "Outfit, sans-serif" },
        }} />
      <UserAuthProvider>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<PublicSite />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/manifesto" element={<ManifestoPage />} />
            <Route path="/drop-club" element={<DropClubPage />} />
            <Route path="/lookbook" element={<LookbookPage />} />
            <Route path="/contatti" element={<ContattiPage />} />
            <Route path="/spedizioni" element={<SpedizioniPage />} />
            <Route path="/resi" element={<ResiPage />} />
            <Route path="/autenticita" element={<AutenticitaPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/product/:id" element={<ProductDetailWrapper />} />
            <Route path="/login" element={<UserLoginPage mode="login" />} />
            <Route path="/register" element={<UserLoginPage mode="register" />} />
            <Route path="/account" element={<CartProvider><AccountPage /><CartDrawer /></CartProvider>} />
            <Route path="/checkout/success" element={<CartProvider><CheckoutSuccessPage /></CartProvider>} />
            <Route path="/checkout/cancel" element={<CartProvider><CheckoutCancelPage /></CartProvider>} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="products/import" element={<ImportProductsPage />} />
              <Route path="products/gsheet" element={<GoogleSheetSyncPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="admins" element={<AdminsPage />} />
              <Route path="newsletter" element={<NewsletterPage />} />
              <Route path="card-requests" element={<CardRequestsPage />} />
              <Route path="crm/pipeline" element={<PipelinePage />} />
              <Route path="crm/contacts" element={<ContactsPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
            </Route>
          </Routes>
        </AuthProvider>
      </UserAuthProvider>
    </BrowserRouter>
  );
}

export default App;
